import React, { useMemo, useCallback, useEffect, useState } from "react";
import { getTextWidth } from "@utils";
import { WIDTHS, ACTION_COLUMN_WIDTH, COLUMN_WIDTH_SAMPLE_SIZE } from "../HyroTable.consts";
import { MenuItem } from "@mui/material";
import HyroThreeDotMenu from "@hyro-components/HyroThreeDotMenu";
import ColumnHeader from "../components/ColumnHeader";
import TableCell from "../components/TableCell";
import { gridClasses } from "@mui/x-data-grid";

const EMPTY_ARRAY = [];

const CELL_FONT = "600 14px Inter, sans-serif";

const getCellValueLength = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "object") {
    return Object.values(value).reduce((max, curr) => Math.max(max, String(curr ?? "").length), 0);
  }
  return String(value).length;
};

const getCellWidth = (value) => {
  if (typeof value === "object" && value !== null) {
    return Object.values(value).reduce(
      (max, curr) => Math.max(max, getTextWidth({ text: curr, font: CELL_FONT, extraWidth: WIDTHS.EXTRA_PADDING })), 0);
  }
  return getTextWidth({ text: value, font: CELL_FONT });
};

export default function useTableData({
  columns = EMPTY_ARRAY,
  rows = EMPTY_ARRAY,
  rowActions,
  onRowUpdate,
  currentEditableField,
  testId,
  columnStyle,
  overrideMaxRowHeight,
  truncateCellText,
  clearSelection,
  apiRef,
  selectedColumn
}) {
  const [isActionsSticky, setIsActionsSticky] = useState(false);
  const normalizedRows = useMemo(() => rows.map((row, index) => ({ index, ...row })), [rows]);
  const selectedColumnIndex = useMemo(() => columns.findIndex(column => column.key === selectedColumn), [columns, selectedColumn]);

  useEffect(() => {
    if (selectedColumn && selectedColumnIndex !== -1 && apiRef.current) {
      const columnElement = apiRef.current.getColumnHeaderElement(columns[selectedColumnIndex].field);
      if (columnElement) {
        columnElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center"
        });
      }
    }
  }, [selectedColumn, selectedColumnIndex, apiRef, columns]);

  useEffect(() => {
    if (!apiRef?.current) return;
    const handleScrollPositionChange = (params) => {
      const gridElement = apiRef.current?.rootElementRef?.current?.querySelector(`.${gridClasses.virtualScroller}`);
      if (gridElement) {
        const maxScrollLeft = gridElement.scrollWidth - gridElement.clientWidth;
        const left = Math.ceil(params.left);
        setIsActionsSticky(left > 0 && left < maxScrollLeft);
      }
    };
    const unsubscribeScrollPositionChange = apiRef.current.subscribeEvent("scrollPositionChange", handleScrollPositionChange);
    return () => {
      unsubscribeScrollPositionChange();
    };
  }, [apiRef]);

  useEffect(() => {
    normalizedRows.forEach((row, idx) => {
      if (row.id === undefined || row.id === null || row.id === "") {
        console.error("HyroTable: Row missing id", row, "at index", idx);
      }
    });
  }, [normalizedRows]);

  const handleRowUpdate = useCallback((newRow, oldRow) => {
    const hasCellChanged = currentEditableField && newRow[currentEditableField] !== oldRow?.[currentEditableField];
    const cellNewValue = newRow[currentEditableField];

    if (hasCellChanged) {
      const updatedRow = onRowUpdate?.({ fieldName: currentEditableField, newValue: cellNewValue, newRow });
      return updatedRow || newRow;
    }

    return newRow;
  }, [currentEditableField, onRowUpdate]);

  const getActionsColumn = useCallback(() => [
    {
      field: "actions",
      headerName: "",
      width: ACTION_COLUMN_WIDTH,
      sortable: false,
      renderCell: ({ row, api }) => {
        const computeRowActions = Array.isArray(rowActions) ? rowActions : rowActions?.(row);
        if (!computeRowActions) return null;

        return (
          <HyroThreeDotMenu
            testId={`${testId}-table-row-${row.index}`}
            onMenuOpen={() => {
              clearSelection?.();
            }}
            menuItems={computeRowActions?.map(({ display, id, run }) => (
              <MenuItem
                key={id}
                data-testid={`${testId}-three-dot-menu-${display.replace(" ", "-")}`}
                onClick={() => run(row)}
              >
                {display}
              </MenuItem>
            ))}
          />
        );
      }
    }
  ], [rowActions, testId, clearSelection]);

  const normalizeColumnsWidth = useCallback((columns) => columns?.map(({ field, headerName, width: overrideWidth, flex, asArray, sizeToHeader }, index) => {
    if (overrideWidth) {
      return {
        ...columns[index],
        minWidth: Math.min(overrideWidth, WIDTHS.COLUMN_MAX_WIDTH),
        flex: flex || 0
      };
    }

    // Canvas text measurement is expensive — measure only the longest cells
    // (by character count) instead of every row.
    // sizeToHeader: cells render a resolved label, not the stored value, so skip content measurement.
    const longestColumnContent = (sizeToHeader ? EMPTY_ARRAY : rows)
      .map(row => {
        const value = Array.isArray(row[field]) ? row[field][0] : row[field];
        return { value, length: getCellValueLength(value) };
      })
      .sort((a, b) => b.length - a.length)
      .slice(0, COLUMN_WIDTH_SAMPLE_SIZE)
      .reduce((max, { value }) => Array.isArray(value) ? max : Math.max(max, getCellWidth(value)), 0);

    const isFieldAnArray = rows.some(row => row[field] && Array.isArray(row[field]));
    const columnWidth = getTextWidth({
      text: headerName,
      extraWidth: WIDTHS.EXTRA_PADDING,
      font: CELL_FONT
    });

    const actualWidth = Math.max(columnWidth, longestColumnContent);
    const finalWidth = isFieldAnArray || asArray
      ? actualWidth + WIDTHS.ARRAY_EXTRA_WIDTH
      : actualWidth + (index === 0 ? WIDTHS.EXTRA_PADDING_FIRST_COLUMN : WIDTHS.EXTRA_PADDING);

    return {
      ...columns[index],
      minWidth: Math.min(finalWidth, WIDTHS.COLUMN_MAX_WIDTH),
      flex: flex ? flex : 1
    };
  }), [rows]);

  const addDefaultRenderCell = useCallback((columns) => columns.map((column, colIndex) => ({
    ...column,
    renderHeader: (params) => (
      <ColumnHeader
        params={params}
        column={column}
        colIndex={colIndex}
        testId={testId}
        columnStyle={columnStyle}
        overrideMaxRowHeight={overrideMaxRowHeight}
      />
    ),
    renderCell: (params) => (
      <TableCell
        params={params}
        column={column}
        colIndex={colIndex}
        truncateCellText={truncateCellText}
        overrideMaxRowHeight={overrideMaxRowHeight}
      />
    ),
    renderEditCell: (params) => (
      <>
        {column.renderEditCell(params)}
      </>
    )
  })), [testId, columnStyle, overrideMaxRowHeight, truncateCellText]);

  const normalizedCols = useMemo(() => {
    const hasRowActions = Array.isArray(rowActions) || rows.some(row => rowActions?.(row));
    const cols = normalizeColumnsWidth(columns);
    if (hasRowActions) {
      cols.push(...getActionsColumn());
    }
    return addDefaultRenderCell(cols);
  }, [columns, rows, rowActions, getActionsColumn, addDefaultRenderCell, normalizeColumnsWidth]);

  return {
    normalizedRows,
    handleRowUpdate,
    normalizedCols,
    isActionsSticky,
    selectedColumnIndex
  };
}
